import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { AppService } from './app.service';
import { App } from 'app/core/app/app.model';
import { filter, map, take } from 'rxjs';

/**
 * Resolver that loads the app context from the route param.
 *
 * Extracts the `projectId` from route params, waits for the apps list to be
 * populated (by `initialDataResolver` on the parent layout route), finds the
 * matching project, and sets it as the active app in AppService.
 *
 * Uses an Observable pipeline with `filter` to safely wait for the apps
 * BehaviorSubject to be populated, avoiding race conditions on deep links.
 *
 * If the project is not found, redirects to /projects.
 */
export const appContextResolver: ResolveFn<App | null> = (route) => {
  const appService = inject(AppService);
  const router = inject(Router);

  const projectId = route.paramMap.get('projectId');
  if (!projectId) {
    router.navigate(['/projects']);
    return null;
  }

  return appService.apps$.pipe(
    // Wait for apps to be loaded (skip the initial null from BehaviorSubject).
    //
    // The test is "loaded", not "non-empty": an account with no projects emits
    // `[]`, which is a complete answer. Waiting for a project to appear meant
    // the resolver never completed, so the navigation never finished and the
    // splash screen — which hides on `NavigationEnd` — stayed up forever. The
    // `!app` branch below is where an empty list belongs.
    filter((apps): apps is App[] => Array.isArray(apps)),
    take(1),
    map((apps) => {
      const app = apps.find((a) => a.id === projectId);
      if (!app) {
        console.warn(
          `Project with ID ${projectId} not found, redirecting to /projects`,
        );
        router.navigate(['/projects']);
        return null;
      }
      appService.setActiveApp(app);
      return app;
    }),
  );
};
